import type { LocationQueryValue } from 'vue-router'
import parsePhoneNumber from 'libphonenumber-js'
import { format as fnsFormat } from 'date-fns'

export const addOffsetToTime = (timeStr?: string | null) => {
  if (!timeStr) return ''
  // timeStr is in format "HH:MM"
  const [hours, minutes] = timeStr.split(':').map(Number)

  // get client offset in hours
  const offsetMinutes = new Date().getTimezoneOffset()
  const offsetHours = -offsetMinutes / 60

  if (hours === undefined) return ''

  // build a Date at today's date with given time
  const now = new Date()
  now.setHours(hours, minutes, 0, 0)

  // add offset
  now.setHours(now.getHours() - offsetHours)

  // format back to "HH:MM"
  const output = now.toTimeString().slice(0, 5)
  return output === 'Inval' ? '' : output
}

export const dataURLtoFile = (dataurl: string, filename: string): File => {
  const arr = dataurl.split(',')
  // @ts-expect-error "Expected"
  const mime = arr[0].match(/:(.*?);/)?.[1]
  // @ts-expect-error "Expected"
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], filename, { type: mime })
}

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export const formatDuration = (value?: number | null): string | undefined => {
  if (value === null || value === undefined) return

  if (value === 0 || isNaN(value)) return '00:00'
  const date = new Date(0)
  date.setSeconds(value)
  return fnsFormat(date, 'mm:ss')
}

export const formatPhoneNumber = (
  value?: string | null
): string | undefined => {
  if (value === null || value === undefined) return

  const pn = parsePhoneNumber(value)

  if (pn === undefined) return

  return pn.formatNational()
}

export const getContentObjectName = (contentType?: {
  app_label?: string
  model?: string
}) => {
  if (!contentType?.app_label || !contentType?.model) return

  const app = GenericFieldName[contentType.app_label]
  if (!app) return
  return app[contentType.model]
}

export const getContentObjectUrl = (
  contentType?: { app_label?: string; model?: string },
  objectId?: number | string
) => {
  if (!contentType?.app_label || !contentType?.model || !objectId) return

  const app = GenericFieldURL[contentType.app_label]
  if (!app) return
  const model = app[contentType.model]
  if (!model) return
  return `${model}${objectId}`
}

export const getCookie = (name: string): string | undefined => {
  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))
    ?.split('=')[1]
  return value ? decodeURIComponent(value) : undefined
}

export const getLocalTime = (timeStr?: string | null) => {
  const date = new Date().toISOString().split('T')[0]
  const dt = parseStringISO(`${date}T${timeStr}.000Z`)

  if (!dt) return null

  const hours = dt.getHours().toString().padStart(2, '0')
  const minutes = dt.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export const getWeekNumber = (date: Date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1)
  const diffInDays = Math.floor(
    (date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)
  )

  return Math.ceil((diffInDays + startOfYear.getDay() + 1) / 7)
}

export const getDateTimeISO = (value: Date) => {
  const offsetMinutes = new Date().getTimezoneOffset()
  const offsetMs = offsetMinutes * 60 * 1000
  const newDate = new Date(value.getTime() - offsetMs)
  return newDate.toISOString()
}

export const getDateTimeISODate = (value?: Date | null) => {
  if (!value) return value
  return getDateTimeISO(value).split('T')[0]
}

export const getDateTimeISOTime = (value?: Date | null) => {
  if (!value) return value
  return getDateTimeISO(value).split('T')[1]
}

export const roundToNearest30 = (value: Date) => {
  const minutes = value.getMinutes()

  if (minutes < 15) {
    value.setMinutes(0, 0, 0)
  } else if (minutes < 45) {
    value.setMinutes(30, 0, 0)
  } else {
    value.setHours(value.getHours() + 1, 0, 0, 0)
  }

  return value
}

export const setupHeaders = (headers: Headers) => {
  const csrfToken = getCookie('csrftoken')

  if (csrfToken) {
    if (Array.isArray(headers)) {
      headers.push(['X-CSRFToken', csrfToken])
    } else if (headers instanceof Headers) {
      headers.set('X-CSRFToken', csrfToken)
    }
  }

  return headers
}

export const toColor = (id: number, isDark: boolean = false) => {
  const GOLDEN_ANGLE = 137.508 // degrees
  const hue = (id * GOLDEN_ANGLE) % 360
  const saturation = 40 // pastel/muted
  // lightness band:
  const lightness = isDark
    ? 30 + ((id * 23) % 11) // between 30–40% in dark
    : 85 + ((id * 17) % 11) // between 85–95% in light

  return `hsl(${hue.toFixed(1)}, ${saturation}%, ${lightness}%)`
}

export function toNumber<TResult = number>(
  value?: LocationQueryValue | LocationQueryValue[] | string | number,
  defaultValue?: number
): TResult {
  defaultValue = defaultValue || 0
  if (!value) return defaultValue as TResult
  value = Array.isArray(value) ? value[0] : value
  const number = Number(value)
  return isNaN(number) ? (defaultValue as TResult) : (number as TResult)
}
